import { isObject, uniq } from 'lodash';

import Font from '../enum/Font';
import { fonts } from '../fonts';

const regEx = new RegExp('-', 'g');

let customFonts = null;

/**
 * The css2 endpoint rejects any request asking for more than 120 distinct
 * families with a 403, before it even validates the family names. Chrome then
 * refuses the HTML error body via CORB and *every* font in the request fails.
 * We chunk well under that so the list can keep growing safely.
 */
export const MAX_FAMILIES_PER_REQUEST = 50;

function resolveFamilies(selectedFonts: Font[]): string[] {

    const families = [];

    for (const selectedFont of selectedFonts) {

        const font = fonts.find(f => f.id == selectedFont);

        if (font == null || font.selfHosted) {
            continue;
        }

        let family = font.family;

        if (font.weights?.length) {

            family += `:wght@${font.weights.join(';')}`;
        }

        families.push(family);
    }

    return uniq(families);
}

function buildGoogleFontsUrl(families: string[]): string {

    const googleFontsUrl = new URL('https://fonts.googleapis.com/css2');
    googleFontsUrl.searchParams.append('display', 'swap');

    for (const family of families) {

        googleFontsUrl.searchParams.append('family', family);
    }

    return googleFontsUrl.href;
}

/**
 * Splits the requested fonts across as many css2 URLs as needed to stay under
 * the per-request family cap. Callers should emit one <link> per URL.
 */
export function generateGoogleFontsUrls(selectedFonts: Font[]): string[] {

    const families = resolveFamilies(selectedFonts);

    if (!families.length) {
        return [];
    }

    const urls = [];

    for (let i = 0; i < families.length; i += MAX_FAMILIES_PER_REQUEST) {

        urls.push(buildGoogleFontsUrl(families.slice(i, i + MAX_FAMILIES_PER_REQUEST)));
    }

    return urls;
}

export function generateGoogleFontsUrl(selectedFonts: Font[]) {

    return buildGoogleFontsUrl(resolveFamilies(selectedFonts));
}

export function generateCustomFontFaces(unfilteredItems) {
    let newStyle = document.createElement('style');

    const filteredItems = unfilteredItems?.filter(i => i.type === "FONT")
        .map(font => {
            const fontName = font.filename.split("-spacer-")[0];
            return (
                {
                    label: fontName.replace(regEx, ' '),
                    family: fontName.replace(regEx, ' '),
                    id: fontName.replace(regEx, ' '),
                    databaseId: font.id,
                    url: font.url,
                    divider: !!font.divider
                }
            )
        }
        );

    for (let i = 0; i < filteredItems?.length; i++) {
        newStyle.appendChild(document.createTextNode("\
        @font-face {\
            font-family: " + filteredItems[i].family + ";\
            src: url('" + filteredItems[i].url + "');\
        }"
        ));
    }

    document.head.appendChild(newStyle);

    customFonts = filteredItems;
}

export function getCustomFonts() {
    return customFonts;
}

export function getFontsFromShow(show, slides, unique = true) {

    if (!slides.length) {
        return [];
    }

    const fonts = slides
        .map(s => s.widgets).flat()
        .map(w => getFontsFromWidget(w, false)).flat();

    if (show.style?.fontPrimary != null) {

        fonts.push(show.style.fontPrimary);
    }

    if (show.style?.fontSecondary != null) {

        fonts.push(show.style.fontSecondary);
    }

    return unique ? uniq(fonts) : fonts;
}

export function getFontsFromWidget(widget, unique = true) {

    return getFontsFromObject(widget.settings, unique);
}

export function getFontsFromObject(value, unique = true): Font[] {

    if (!isObject(value)) {
        return [];
    }

    if (value.__typename === 'WidgetTextStyle') {
        return value.font !== Font.PRIMARY && value.font !== Font.SECONDARY ? [value.font] : [];
    }

    const fonts = Object.values(value)
        .map(v => getFontsFromObject(v, unique))
        .flat();

    return unique ? uniq(fonts) : fonts;
}
